import { ChevronLeft, ChevronRight } from "@untitled-ui/icons-react";
import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import type { SwiperClass } from "swiper/react";

interface ProgressPaginationProps {
  swiper: SwiperClass | null;
  totalSlides: number;
  autoplayDelay: number;
  onPrev?: () => void;
  onNext?: () => void;
}

export default function ProgressPagination({
  swiper,
  totalSlides,
  autoplayDelay,
  onPrev,
  onNext,
}: ProgressPaginationProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const startTimeRef = useRef<number>(Date.now());
  const animationFrameRef = useRef<number | null>(null);

  useEffect(() => {
    if (!swiper) return;

    const handleSlideChange = () => {
      const newIndex = swiper.realIndex;
      setActiveIndex(newIndex);
      setProgress(0);
      startTimeRef.current = Date.now();
    };

    const handleAutoplayStart = () => {
      setProgress(0);
      startTimeRef.current = Date.now();
    };

    const handleAutoplayStop = () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };

    swiper.on("slideChange", handleSlideChange);
    swiper.on("autoplayStart", handleAutoplayStart);
    swiper.on("autoplayStop", handleAutoplayStop);

    setActiveIndex(swiper.realIndex);
    startTimeRef.current = Date.now();

    return () => {
      swiper.off("slideChange", handleSlideChange);
      swiper.off("autoplayStart", handleAutoplayStart);
      swiper.off("autoplayStop", handleAutoplayStop);
    };
  }, [swiper]);

  useEffect(() => {
    if (!swiper) return;

    const updateProgress = () => {
      if (!swiper.autoplay?.running) {
        setProgress(0);
        return;
      }

      const elapsed = Date.now() - startTimeRef.current;
      const newProgress = Math.min((elapsed / autoplayDelay) * 100, 100);
      setProgress(newProgress);

      if (newProgress < 100) {
        animationFrameRef.current = requestAnimationFrame(updateProgress);
      }
    };

    animationFrameRef.current = requestAnimationFrame(updateProgress);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [swiper, activeIndex, autoplayDelay]);

  const getOpacity = (_index: number, visibleIndex: number) => {
    const distance = Math.abs(visibleIndex - 2);
    if (distance === 0) return 1;
    if (distance === 1) return 0.7;
    if (distance === 2) return 0.3;
    return 0.1;
  };

  const getVisibleDots = () => {
    if (totalSlides <= 5) {
      return Array.from({ length: totalSlides }, (_, i) => ({
        index: i,
        visibleIndex: i,
      }));
    }

    const dots: Array<{ index: number; visibleIndex: number }> = [];
    let startIndex = Math.max(0, activeIndex - 2);
    let endIndex = Math.min(totalSlides - 1, activeIndex + 2);

    if (activeIndex < 2) {
      startIndex = 0;
      endIndex = 4;
    } else if (activeIndex > totalSlides - 3) {
      startIndex = totalSlides - 5;
      endIndex = totalSlides - 1;
    }

    for (let i = startIndex; i <= endIndex; i++) {
      dots.push({
        index: i,
        visibleIndex: i - startIndex,
      });
    }

    return dots;
  };

  const visibleDots = getVisibleDots();

  return (
    <div className="relative mt-8 flex items-center justify-center gap-2">
      {onPrev && (
        <ChevronLeft
          onClick={onPrev}
          className="size-4 text-gray-500 rtl:rotate-180"
        />
      )}

      {visibleDots.map(({ index, visibleIndex }) => {
        const isActive = index === activeIndex;
        const opacity = getOpacity(index, visibleIndex);

        return (
          <button
            key={index}
            onClick={() => {
              if (swiper) {
                swiper.slideToLoop(index);
              }
            }}
            className={classNames(
              "cursor-pointer border border-gray-300 transition-all duration-300",
              isActive
                ? "relative h-2 w-16 rounded-full"
                : "h-2 w-2 rounded-full",
            )}
            style={{
              backgroundColor: isActive ? "#fff" : "#d0d5dd",
              opacity: isActive ? 1 : opacity,
            }}
          >
            {isActive && (
              <div
                className="bg-primary-600 absolute top-0 left-0 h-full rounded-full transition-all duration-75 ease-linear"
                style={{
                  width: `${progress}%`,
                }}
              />
            )}
          </button>
        );
      })}

      {onNext && (
        <ChevronRight
          onClick={onNext}
          className="size-4 text-gray-500 rtl:rotate-180"
        />
      )}
    </div>
  );
}
